import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiFileText,
  FiLock,
  FiShield,
  FiEye,
  FiDownload,
  FiCalendar,
  FiMail,
  FiUser,
  FiHome,
  FiGlobe,
  FiCheckCircle,
  FiAlertCircle,
  FiChevronRight,
  FiChevronDown,
  FiClock,
  FiRefreshCw,
  FiTrash2,
  FiEdit,
  FiBook,
  FiScale,
  FiFlag,
  FiBell,
  FiMessageSquare,
  FiExternalLink,
} from "react-icons/fi";
import { MdOutlineGavel } from "react-icons/md";
import terms from "../assets/Terms_and_Conditions_asplanner.pdf";
import LNavbar from "../landingPage/LNavbar";

const TermsAndConditions = () => {
  const [lastUpdated] = useState("2025-12-11");
  const [effectiveDate] = useState("2025-12-11");
  const [acceptedDate, setAcceptedDate] = useState(null);
  const [activeSection, setActiveSection] = useState("intro");
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const contentRef = useRef(null);

  const sections = [
    { id: "intro", title: "Introduction & Acceptance" },
    { id: "definitions", title: "Definitions" },
    { id: "accounts", title: "Account Registration & Security" },
    { id: "subscriptions", title: "Subscription & Billing" },
    { id: "usage", title: "Acceptable Use" },
    { id: "content", title: "User Content & Rights" },
    { id: "privacy", title: "Privacy & Data Protection" },
    { id: "termination", title: "Termination" },
    { id: "liability", title: "Limitation of Liability" },
    { id: "disputes", title: "Dispute Resolution" },
    { id: "changes", title: "Changes to Terms" },
    { id: "contact", title: "Contact Information" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const container = contentRef.current;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight - container.clientHeight;
      const progress = (scrollTop / scrollHeight) * 100;
      setReadingProgress(progress);

      if (progress > 95) {
        setHasScrolledToBottom(true);
      }

      // Find active section based on scroll position
      const sectionElements = sections
        .map((section) => document.getElementById(section.id))
        .filter(Boolean);

      let currentSection = "intro";
      for (const section of sectionElements) {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 100) {
          currentSection = section.id;
        }
      }
      setActiveSection(currentSection);
    };

    const container = contentRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
    }
  };

  const handleAccept = () => {
    const today = new Date().toISOString().split("T")[0];
    setAcceptedDate(today);
    // In a real app, you would save this to localStorage/backend
    localStorage.setItem("termsAccepted", today);
  };

  return (
    <TermsContainer>
      <LNavbar />
      <LegalBackground />

      <LegalLayout>
        {/* Side Navigation */}
        <SideNavigation>
          <NavHeader>
            <MdOutlineGavel />
            <span>Terms & Conditions</span>
          </NavHeader>

          <NavContent>
            <QuickInfo>
              <InfoItem>
                <FiCalendar />
                <div>
                  <label>Last Updated</label>
                  <span>{lastUpdated}</span>
                </div>
              </InfoItem>
              <InfoItem>
                <FiUser />
                <div>
                  <label>Version</label>
                  <span>3.2.1</span>
                </div>
              </InfoItem>
            </QuickInfo>

            <ProgressSection>
              <ProgressHeader>
                <span>Reading Progress</span>
                <span>{readingProgress.toFixed(0)}%</span>
              </ProgressHeader>
              <ProgressBar>
                <ProgressFill progress={readingProgress} />
              </ProgressBar>
            </ProgressSection>

            <NavigationMenu>
              <h4>Table of Contents</h4>
              <NavList>
                {sections.map((section, index) => (
                  <NavItem
                    key={section.id}
                    active={activeSection === section.id}
                    onClick={() => scrollToSection(section.id)}
                  >
                    <NavNumber>{String(index + 1).padStart(2, "0")}</NavNumber>
                    <NavTitle>
                      {section.title}
                      {activeSection === section.id && <ActiveIndicator />}
                    </NavTitle>
                  </NavItem>
                ))}
              </NavList>
            </NavigationMenu>

            <DownloadSection>
              <DownloadButton
                href={terms}
                download="Terms_and_Conditions_asplanner.pdf"
              >
                <FiDownload />
                Download PDF Version
              </DownloadButton>

              <Disclaimer>
                <FiAlertCircle />
                <span>Please read carefully before accepting</span>
              </Disclaimer>
            </DownloadSection>
          </NavContent>
        </SideNavigation>

        {/* Main Content */}
        <MainContent>
          <ContentWrapper ref={contentRef}>
            <ContentHeader>
              <h1>Terms & Conditions</h1>
              <p>Last Updated: {lastUpdated}</p>
            </ContentHeader>

            {/* Introduction */}
            <LegalSection id="intro">
              <SectionHeader>
                <SectionNumber>01</SectionNumber>
                <div>
                  <h2>Introduction & Acceptance</h2>
                  <SectionSubtitle>Welcome to asplanner.pro</SectionSubtitle>
                </div>
              </SectionHeader>

              <SectionContent>
                <p>
                  Welcome to <strong>asplanner.pro</strong> ("we," "our," or
                  "us"). These Terms and Conditions ("Terms") govern your access
                  to and use of our website, mobile applications, and services
                  (collectively, the "Services").
                </p>

                <ImportantNote>
                  <FiAlertCircle />
                  <div>
                    <strong>Important Notice:</strong> By accessing or using our
                    Services, you agree to be bound by these Terms. If you
                    disagree with any part of these Terms, you may not access
                    the Services.
                  </div>
                </ImportantNote>

                <p>
                  These Terms constitute a legally binding agreement between you
                  and asplanner.pro. If you are using the Services on behalf of
                  an organization, you represent that you have the authority to
                  bind that organization to these Terms.
                </p>
              </SectionContent>
            </LegalSection>

            {/* Definitions */}
            <LegalSection id="definitions">
              <SectionHeader>
                <SectionNumber>02</SectionNumber>
                <div>
                  <h2>Definitions</h2>
                  <SectionSubtitle>
                    Key terms used in this agreement
                  </SectionSubtitle>
                </div>
              </SectionHeader>

              <SectionContent>
                <DefinitionGrid>
                  <DefinitionCard>
                    <dt>"Services"</dt>
                    <dd>
                      All products, features, content, and applications offered
                      by asplanner.pro
                    </dd>
                  </DefinitionCard>
                  <DefinitionCard>
                    <dt>"User" / "You"</dt>
                    <dd>
                      Any individual or entity accessing or using the Services
                    </dd>
                  </DefinitionCard>
                  <DefinitionCard>
                    <dt>"Content"</dt>
                    <dd>
                      Text, images, data, information, and other materials
                      uploaded to the Services
                    </dd>
                  </DefinitionCard>
                  <DefinitionCard>
                    <dt>"Subscription"</dt>
                    <dd>
                      Paid access to premium features for a specified period
                    </dd>
                  </DefinitionCard>
                  <DefinitionCard>
                    <dt>"Free Plan"</dt>
                    <dd>
                      Basic access to Services without payment requirement
                    </dd>
                  </DefinitionCard>
                  <DefinitionCard>
                    <dt>"Pro Plan"</dt>
                    <dd>
                      Paid subscription with access to all premium features
                    </dd>
                  </DefinitionCard>
                </DefinitionGrid>
              </SectionContent>
            </LegalSection>

            {/* Account Registration */}
            <LegalSection id="accounts">
              <SectionHeader>
                <SectionNumber>03</SectionNumber>
                <div>
                  <h2>Account Registration & Security</h2>
                  <SectionSubtitle>
                    Your account responsibilities
                  </SectionSubtitle>
                </div>
              </SectionHeader>

              <SectionContent>
                <p>
                  To access certain features, you must register for an account.
                  You agree to:
                </p>

                <RequirementsGrid>
                  <RequirementCard>
                    <FiCheckCircle />
                    <h4>Provide Accurate Information</h4>
                    <p>
                      Supply accurate, current, and complete information during
                      registration
                    </p>
                  </RequirementCard>
                  <RequirementCard>
                    <FiLock />
                    <h4>Maintain Security</h4>
                    <p>
                      Keep your password secure and confidential at all times
                    </p>
                  </RequirementCard>
                  <RequirementCard>
                    <FiUser />
                    <h4>Account Responsibility</h4>
                    <p>
                      Accept responsibility for all activities under your
                      account
                    </p>
                  </RequirementCard>
                  <RequirementCard>
                    <FiBell />
                    <h4>Report Issues</h4>
                    <p>
                      Notify us immediately of any unauthorized access or
                      security breach
                    </p>
                  </RequirementCard>
                </RequirementsGrid>

                <SecurityNotice>
                  <FiShield />
                  <div>
                    <h4>Security Notice</h4>
                    <p>
                      You are responsible for safeguarding your password and for
                      any activities or actions under your account. We recommend
                      using a strong password and never sharing your credentials
                      with others.
                    </p>
                  </div>
                </SecurityNotice>
              </SectionContent>
            </LegalSection>

            {/* Subscription & Billing */}
            <LegalSection id="subscriptions">
              <SectionHeader>
                <SectionNumber>04</SectionNumber>
                <div>
                  <h2>Subscription & Billing</h2>
                  <SectionSubtitle>
                    Payment terms and conditions
                  </SectionSubtitle>
                </div>
              </SectionHeader>

              <SectionContent>
                <PlanComparison>
                  <PlanCard>
                    <PlanHeader>
                      <h3>Free Plan</h3>
                      <Price>€0/month</Price>
                    </PlanHeader>
                    <PlanFeatures>
                      <Feature included>Basic task management</Feature>
                      <Feature included>Habit tracking</Feature>
                      <Feature included>Simple financial logging</Feature>
                      <Feature excluded>Advanced analytics</Feature>
                      <Feature excluded>Priority support</Feature>
                    </PlanFeatures>
                  </PlanCard>

                  <PlanCard accent>
                    <PlanHeader>
                      <h3>Pro Plan</h3>
                      <Price>€4.99/month</Price>
                    </PlanHeader>
                    <PlanFeatures>
                      <Feature included>Advanced task management</Feature>
                      <Feature included>Smart habit ecosystem</Feature>
                      <Feature included>Financial intelligence</Feature>
                      <Feature included>Advanced analytics</Feature>
                      <Feature included>Priority support</Feature>
                    </PlanFeatures>
                  </PlanCard>
                </PlanComparison>

                <BillingTerms>
                  <h3>Billing Terms & Conditions</h3>
                  <TermsList>
                    <Term>
                      <FiCalendar />
                      <div>
                        <h4>Subscription Renewal</h4>
                        <p>
                          Subscriptions automatically renew unless canceled at
                          least 24 hours before the renewal date.
                        </p>
                      </div>
                    </Term>
                    <Term>
                      <FiExternalLink />
                      <div>
                        <h4>Cancellation Policy</h4>
                        <p>
                          You can cancel your subscription anytime through your
                          account settings. No partial refunds for unused time.
                        </p>
                      </div>
                    </Term>
                    <Term>
                      <FiShield />
                      <div>
                        <h4>Refund Policy</h4>
                        <p>
                          <strong>No automatic refunds.</strong> Refund requests
                          must be submitted directly to info@asitesagency.com
                          for manual review. Refunds are granted at our sole
                          discretion.
                        </p>
                      </div>
                    </Term>
                    <Term>
                      <FiBell />
                      <div>
                        <h4>Price Changes</h4>
                        <p>
                          We reserve the right to change prices with 30-day
                          notice to active subscribers.
                        </p>
                      </div>
                    </Term>
                  </TermsList>
                </BillingTerms>
              </SectionContent>
            </LegalSection>

            {/* Acceptable Use */}
            <LegalSection id="usage">
              <SectionHeader>
                <SectionNumber>05</SectionNumber>
                <div>
                  <h2>Acceptable Use</h2>
                  <SectionSubtitle>Prohibited activities</SectionSubtitle>
                </div>
              </SectionHeader>

              <SectionContent>
                <ProhibitedGrid>
                  <ProhibitedItem>
                    <FiAlertCircle />
                    <div>
                      <h4>Illegal Activities</h4>
                      <p>
                        Any illegal activities or promotion of illegal content
                      </p>
                    </div>
                  </ProhibitedItem>
                  <ProhibitedItem>
                    <FiAlertCircle />
                    <div>
                      <h4>Harassment</h4>
                      <p>Harassment, abuse, or threats against others</p>
                    </div>
                  </ProhibitedItem>
                  <ProhibitedItem>
                    <FiAlertCircle />
                    <div>
                      <h4>Spamming</h4>
                      <p>Spamming or unsolicited communications</p>
                    </div>
                  </ProhibitedItem>
                  <ProhibitedItem>
                    <FiAlertCircle />
                    <div>
                      <h4>Security Breaches</h4>
                      <p>
                        Attempting to compromise system security or integrity
                      </p>
                    </div>
                  </ProhibitedItem>
                </ProhibitedGrid>

                <p>
                  We reserve the right to investigate and take appropriate
                  action against anyone who, in our sole discretion, violates
                  these Terms, including removing content, suspending or
                  terminating accounts, and reporting to law enforcement.
                </p>
              </SectionContent>
            </LegalSection>

            {/* User Content */}
            <LegalSection id="content">
              <SectionHeader>
                <SectionNumber>06</SectionNumber>
                <div>
                  <h2>User Content & Rights</h2>
                  <SectionSubtitle>Your data, your rights</SectionSubtitle>
                </div>
              </SectionHeader>

              <SectionContent>
                <RightsComparison>
                  <RightsColumn>
                    <h3>Your Rights</h3>
                    <RightsList>
                      <li>You retain ownership of your content</li>
                      <li>You grant us license to host and display it</li>
                      <li>You can export your data at any time</li>
                      <li>You can delete your content permanently</li>
                    </RightsList>
                  </RightsColumn>

                  <RightsColumn>
                    <h3>Our Rights</h3>
                    <RightsList>
                      <li>Right to remove violating content</li>
                      <li>Right to analyze usage patterns</li>
                      <li>Right to backup and secure data</li>
                      <li>Right to display content to you</li>
                    </RightsList>
                  </RightsColumn>
                </RightsComparison>
              </SectionContent>
            </LegalSection>

            {/* Privacy */}
            <LegalSection id="privacy">
              <SectionHeader>
                <SectionNumber>07</SectionNumber>
                <div>
                  <h2>Privacy & Data Protection</h2>
                  <SectionSubtitle>How we handle your data</SectionSubtitle>
                </div>
              </SectionHeader>

              <SectionContent>
                <PrivacyPrinciples>
                  <PrivacyCard>
                    <FiEye />
                    <h4>Transparency</h4>
                    <p>We clearly explain what data we collect and why</p>
                  </PrivacyCard>
                  <PrivacyCard>
                    <FiLock />
                    <h4>Security</h4>
                    <p>Enterprise-grade encryption and security measures</p>
                  </PrivacyCard>
                  <PrivacyCard>
                    <FiTrash2 />
                    <h4>Data Minimization</h4>
                    <p>We only collect what's necessary for our Services</p>
                  </PrivacyCard>
                  <PrivacyCard>
                    <FiUser />
                    <h4>Control</h4>
                    <p>You control your data and privacy settings</p>
                  </PrivacyCard>
                </PrivacyPrinciples>

                <p>
                  Our Privacy Policy provides detailed information about how we
                  collect, use, and protect your personal information. By using
                  our Services, you agree to our collection and use of data as
                  described in the Privacy Policy.
                </p>
              </SectionContent>
            </LegalSection>

            {/* Termination */}
            <LegalSection id="termination">
              <SectionHeader>
                <SectionNumber>08</SectionNumber>
                <div>
                  <h2>Termination</h2>
                  <SectionSubtitle>How agreements can end</SectionSubtitle>
                </div>
              </SectionHeader>

              <SectionContent>
                <TerminationComparison>
                  <TerminationCard>
                    <h3>By You</h3>
                    <p>You may stop using our Services at any time</p>
                    <ul>
                      <li>Cancel subscription anytime</li>
                      <li>Delete your account permanently</li>
                      <li>Export your data before deletion</li>
                    </ul>
                  </TerminationCard>

                  <TerminationCard>
                    <h3>By Us</h3>
                    <p>We may suspend or terminate access if:</p>
                    <ul>
                      <li>You violate these Terms</li>
                      <li>Required by law</li>
                      <li>Prolonged inactivity (12+ months)</li>
                    </ul>
                  </TerminationCard>
                </TerminationComparison>
              </SectionContent>
            </LegalSection>

            {/* Liability */}
            <LegalSection id="liability">
              <SectionHeader>
                <SectionNumber>09</SectionNumber>
                <div>
                  <h2>Limitation of Liability</h2>
                  <SectionSubtitle>Important legal limitations</SectionSubtitle>
                </div>
              </SectionHeader>

              <SectionContent>
                <LiabilityWarning>
                  <FiAlertCircle />
                  <div>
                    <h4>Important Limitation</h4>
                    <p>
                      To the maximum extent permitted by law, asplanner.pro
                      shall not be liable for any indirect, incidental, special,
                      consequential, or punitive damages, or any loss of profits
                      or revenues, whether incurred directly or indirectly, or
                      any loss of data, use, goodwill, or other intangible
                      losses.
                    </p>
                  </div>
                </LiabilityWarning>

                <p>
                  Our total liability in any matter arising out of or related to
                  these Terms is limited to the greater of (a) the amount you
                  paid us for the Services during the three-month period
                  preceding the event giving rise to liability, or (b) €100.
                </p>
              </SectionContent>
            </LegalSection>

            {/* Disputes */}
            <LegalSection id="disputes">
              <SectionHeader>
                <SectionNumber>10</SectionNumber>
                <div>
                  <h2>Dispute Resolution</h2>
                  <SectionSubtitle>How we resolve conflicts</SectionSubtitle>
                </div>
              </SectionHeader>

              <SectionContent>
                <DisputeProcess>
                  <ProcessStep>
                    <StepNumber>1</StepNumber>
                    <div>
                      <h4>Contact Us</h4>
                      <p>Email info@asitesagency.com with your concern</p>
                    </div>
                  </ProcessStep>
                  <ProcessStep>
                    <StepNumber>2</StepNumber>
                    <div>
                      <h4>Negotiation</h4>
                      <p>30-day period to resolve dispute informally</p>
                    </div>
                  </ProcessStep>
                  <ProcessStep>
                    <StepNumber>3</StepNumber>
                    <div>
                      <h4>Mediation</h4>
                      <p>
                        If unresolved, proceed to mediation in Podujeva, Kosovo
                      </p>
                    </div>
                  </ProcessStep>
                </DisputeProcess>
              </SectionContent>
            </LegalSection>

            {/* Changes to Terms */}
            <LegalSection id="changes">
              <SectionHeader>
                <SectionNumber>11</SectionNumber>
                <div>
                  <h2>Changes to Terms</h2>
                  <SectionSubtitle>How we update these terms</SectionSubtitle>
                </div>
              </SectionHeader>

              <SectionContent>
                <UpdateProcess>
                  <UpdateStep>
                    <FiBell />
                    <div>
                      <h4>Notification</h4>
                      <p>We'll notify you 30 days before changes take effect</p>
                    </div>
                  </UpdateStep>
                  <UpdateStep>
                    <FiRefreshCw />
                    <div>
                      <h4>Review Period</h4>
                      <p>
                        You have 30 days to review changes before they apply
                      </p>
                    </div>
                  </UpdateStep>
                  <UpdateStep>
                    <FiCheckCircle />
                    <div>
                      <h4>Acceptance</h4>
                      <p>Continued use constitutes acceptance of new Terms</p>
                    </div>
                  </UpdateStep>
                </UpdateProcess>
              </SectionContent>
            </LegalSection>

            {/* Contact Information */}
            <LegalSection id="contact">
              <SectionHeader>
                <SectionNumber>12</SectionNumber>
                <div>
                  <h2>Contact Information</h2>
                  <SectionSubtitle>How to reach us</SectionSubtitle>
                </div>
              </SectionHeader>

              <SectionContent>
                <ContactGrid>
                  <ContactCard>
                    <FiMail />
                    <div>
                      <h4>Email Support</h4>
                      <a href="mailto:info@asitesagency.com">
                        info@asitesagency.com
                      </a>
                      <p>For all inquiries and support requests</p>
                    </div>
                  </ContactCard>
                  <ContactCard>
                    <FiHome />
                    <div>
                      <h4>Registered Address</h4>
                      <p>Podujeva, Kosovo</p>
                      <p>Our legal and operational headquarters</p>
                    </div>
                  </ContactCard>
                  <ContactCard>
                    <FiGlobe />
                    <div>
                      <h4>Website</h4>
                      <a
                        href="https://asplanner.pro"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        asplanner.pro
                      </a>
                      <p>Visit our main website for more information</p>
                    </div>
                  </ContactCard>
                </ContactGrid>
              </SectionContent>
            </LegalSection>

            {/* Acceptance Section */}
            <AcceptanceSection>
              <AcceptanceHeader>
                <h2>Acceptance & Agreement</h2>
                <p>Final step to use our Services</p>
              </AcceptanceHeader>

              {acceptedDate ? (
                <AcceptedStatus>
                  <FiCheckCircle />
                  <div>
                    <h3>Terms Accepted</h3>
                    <p>You accepted these Terms on {acceptedDate}</p>
                  </div>
                </AcceptedStatus>
              ) : (
                <AcceptanceContent>
                  <p>
                    By using our Services, you acknowledge that you have read,
                    understood, and agree to be bound by these Terms and
                    Conditions.
                  </p>

                  <AcceptanceNotice>
                    <FiAlertCircle />
                    <div>
                      <strong>Important:</strong> By accepting these terms, you
                      agree to our refund policy which requires direct contact
                      with info@asitesagency.com for any refund requests. No
                      automatic refunds are provided.
                    </div>
                  </AcceptanceNotice>

                  <AcceptanceActions>
                    <DeclineButton onClick={() => window.history.back()}>
                      Decline
                    </DeclineButton>

                    {hasScrolledToBottom ? (
                      <AcceptButton onClick={handleAccept}>
                        <FiCheckCircle />
                        Accept Terms
                      </AcceptButton>
                    ) : (
                      <ScrollNotice>
                        <FiChevronDown />
                        <span>
                          Please read all terms before accepting (scroll to
                          bottom)
                        </span>
                      </ScrollNotice>
                    )}
                  </AcceptanceActions>
                </AcceptanceContent>
              )}
            </AcceptanceSection>
          </ContentWrapper>
        </MainContent>
      </LegalLayout>
    </TermsContainer>
  );
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const TermsContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  padding: 2rem;
  position: relative;

  @media (max-width: 1024px) {
    padding: 1rem;
  }
`;

const LegalBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 10% 20%,
      rgba(99, 102, 241, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 90% 80%,
      rgba(139, 92, 246, 0.05) 0%,
      transparent 50%
    );
  z-index: 0;
`;

const LegalLayout = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  margin-top: 5em;
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 2rem;
  min-height: calc(100vh - 4rem);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SideNavigation = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(20px);
  height: fit-content;
  position: sticky;
  top: 2rem;

  @media (max-width: 1024px) {
    position: static;
    margin-bottom: 2rem;
  }
`;

const NavHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  svg {
    color: #8b5cf6;
    font-size: 1.5rem;
  }

  span {
    color: #ffffff;
    font-size: 1.25rem;
    font-weight: 600;
  }
`;

const NavContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const QuickInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    color: #8b5cf6;
    font-size: 1.25rem;
  }

  div {
    display: flex;
    flex-direction: column;
  }

  label {
    color: #94a3b8;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  span {
    color: #ffffff;
    font-size: 0.9375rem;
    font-weight: 600;
  }
`;

const ProgressSection = styled.div`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  span {
    color: #94a3b8;
    font-size: 0.875rem;
    font-weight: 500;

    &:last-child {
      color: #8b5cf6;
      font-weight: 600;
    }
  }
`;

const ProgressBar = styled.div`
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ progress }) => progress}%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  transition: width 0.3s ease;
`;

const NavigationMenu = styled.div`
  h4 {
    color: #ffffff;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    padding-left: 0.5rem;
  }
`;

const NavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: ${({ active }) =>
    active ? "rgba(139, 92, 246, 0.1)" : "transparent"};
  border: 1px solid
    ${({ active }) =>
      active ? "rgba(139, 92, 246, 0.2)" : "rgba(255, 255, 255, 0.05)"};
  border-radius: 10px;
  color: ${({ active }) => (active ? "#ffffff" : "#94a3b8")};
  font-size: 0.875rem;
  font-weight: ${({ active }) => (active ? "600" : "500")};
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    background: rgba(139, 92, 246, 0.1);
    border-color: rgba(139, 92, 246, 0.3);
    color: #ffffff;
    transform: translateX(5px);
  }
`;

const NavNumber = styled.span`
  color: ${({ active }) => (active ? "#8b5cf6" : "#64748b")};
  font-weight: 700;
  font-size: 0.75rem;
  min-width: 24px;
`;

const NavTitle = styled.span`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ActiveIndicator = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #8b5cf6;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const DownloadSection = styled.div`
  margin-top: auto;
`;

const DownloadButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: #94a3b8;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    border-color: rgba(139, 92, 246, 0.3);
  }
`;

const Disclaimer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 8px;

  svg {
    color: #f59e0b;
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  span {
    color: #fbbf24;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.4;
  }
`;

const MainContent = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  overflow: hidden;
  backdrop-filter: blur(20px);
`;

const ContentWrapper = styled.div`
  height: calc(100vh - 8rem);
  overflow-y: auto;
  padding: 3rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(139, 92, 246, 0.5);
    border-radius: 4px;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    height: auto;
    max-height: calc(100vh - 4rem);
  }
`;

const ContentHeader = styled.div`
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  h1 {
    color: #ffffff;
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 0.5rem;
  }

  p {
    color: #94a3b8;
    font-size: 1rem;
  }
`;

const LegalSection = styled.section`
  margin-bottom: 4rem;
  scroll-margin-top: 2rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const SectionNumber = styled.span`
  color: #8b5cf6;
  font-size: 1.5rem;
  font-weight: 800;
  min-width: 60px;
`;

const SectionSubtitle = styled.p`
  color: #94a3b8;
  font-size: 1rem;
  margin-top: 0.25rem;
`;

const SectionContent = styled.div`
  color: #94a3b8;
  line-height: 1.7;

  p {
    margin-bottom: 1.5rem;
    font-size: 1.0625rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #ffffff;
    font-weight: 600;
  }
`;

const ImportantNote = styled.div`
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  display: flex;
  gap: 1rem;

  svg {
    color: #f59e0b;
    font-size: 1.5rem;
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  strong {
    color: #f59e0b;
  }

  div {
    color: #fbbf24;
  }
`;

const DefinitionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin: 2rem 0;
`;

const DefinitionCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;

  dt {
    color: #8b5cf6;
    font-weight: 700;
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }

  dd {
    color: #94a3b8;
    margin: 0;
    font-size: 0.9375rem;
    line-height: 1.6;
  }
`;

const RequirementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const RequirementCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(139, 92, 246, 0.3);
    transform: translateY(-3px);
  }

  svg {
    color: #10b981;
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  h4 {
    color: #ffffff;
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: #94a3b8;
    margin: 0;
    font-size: 0.9375rem;
  }
`;

const SecurityNotice = styled.div`
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0;
  display: flex;
  gap: 1.5rem;

  svg {
    color: #10b981;
    font-size: 1.5rem;
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  h4 {
    color: #10b981;
    margin-bottom: 0.5rem;
  }

  p {
    color: #34d399;
    margin: 0;
  }
`;

const PlanComparison = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 2rem 0;
`;

const PlanCard = styled.div`
  background: ${({ accent }) =>
    accent
      ? "linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(99, 102, 241, 0.05))"
      : "rgba(255, 255, 255, 0.03)"};
  border: 1px solid
    ${({ accent }) =>
      accent ? "rgba(139, 92, 246, 0.2)" : "rgba(255, 255, 255, 0.1)"};
  border-radius: 16px;
  padding: 2rem;
`;

const PlanHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  h3 {
    color: #ffffff;
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }
`;

const Price = styled.div`
  color: #8b5cf6;
  font-size: 1.75rem;
  font-weight: 700;
`;

const PlanFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ included }) => (included ? "#94a3b8" : "#64748b")};
  font-size: 0.9375rem;

  &::before {
    content: "${({ included }) => (included ? "✓" : "✗")}";
    color: ${({ included }) => (included ? "#10b981" : "#ef4444")};
    font-weight: bold;
  }
`;

const BillingTerms = styled.div`
  margin: 3rem 0;
`;

const TermsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const Term = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;

  svg {
    color: #8b5cf6;
    font-size: 1.5rem;
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  h4 {
    color: #ffffff;
    margin-bottom: 0.5rem;
    font-size: 1.125rem;
  }

  p {
    color: #94a3b8;
    margin: 0;
    font-size: 0.9375rem;
    line-height: 1.6;

    strong {
      color: #ef4444;
    }
  }
`;

const ProhibitedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const ProhibitedItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.1);
  border-radius: 12px;

  svg {
    color: #ef4444;
    font-size: 1.5rem;
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  h4 {
    color: #fca5a5;
    margin-bottom: 0.5rem;
    font-size: 1.125rem;
  }

  p {
    color: #fca5a5;
    margin: 0;
    font-size: 0.9375rem;
    opacity: 0.9;
  }
`;

const RightsComparison = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 2rem 0;
`;

const RightsColumn = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;

  h3 {
    color: #ffffff;
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
    text-align: center;
  }
`;

const RightsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    color: #94a3b8;
    padding: 0.75rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    font-size: 0.9375rem;
    line-height: 1.6;

    &:last-child {
      border-bottom: none;
    }

    &::before {
      content: "•";
      color: #8b5cf6;
      font-weight: bold;
      display: inline-block;
      width: 1em;
      margin-left: -1em;
    }
  }
`;

const PrivacyPrinciples = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const PrivacyCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(139, 92, 246, 0.3);
    transform: translateY(-3px);
  }

  svg {
    color: #8b5cf6;
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  h4 {
    color: #ffffff;
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: #94a3b8;
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const TerminationComparison = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 2rem 0;
`;

const TerminationCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;

  h3 {
    color: #ffffff;
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: #94a3b8;
    margin-bottom: 1.5rem;
    font-size: 1rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    color: #94a3b8;
    padding: 0.5rem 0;
    font-size: 0.9375rem;
    line-height: 1.6;

    &::before {
      content: "•";
      color: #8b5cf6;
      font-weight: bold;
      display: inline-block;
      width: 1em;
      margin-left: -1em;
    }
  }
`;

const LiabilityWarning = styled.div`
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0;
  display: flex;
  gap: 1.5rem;

  svg {
    color: #f59e0b;
    font-size: 1.5rem;
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  h4 {
    color: #f59e0b;
    margin-bottom: 0.5rem;
  }

  p {
    color: #fbbf24;
    margin: 0;
  }
`;

const DisputeProcess = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin: 2rem 0;
`;

const ProcessStep = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;

  &:hover {
    border-color: rgba(139, 92, 246, 0.2);
  }
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.125rem;
  flex-shrink: 0;
`;

const UpdateProcess = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 2rem 0;
`;

const UpdateStep = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;

  svg {
    color: #8b5cf6;
    font-size: 1.25rem;
    flex-shrink: 0;
  }

  h4 {
    color: #ffffff;
    margin-bottom: 0.25rem;
    font-size: 1rem;
  }

  p {
    color: #94a3b8;
    margin: 0;
    font-size: 0.875rem;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const ContactCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(139, 92, 246, 0.3);
    transform: translateY(-3px);
  }

  svg {
    color: #8b5cf6;
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  h4 {
    color: #ffffff;
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
  }

  a {
    color: #8b5cf6;
    text-decoration: none;
    font-size: 0.9375rem;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }

  p {
    color: #94a3b8;
    margin: 0.5rem 0 0 0;
    font-size: 0.875rem;
  }
`;

const AcceptanceSection = styled.div`
  margin-top: 4rem;
  padding-top: 3rem;
  border-top: 2px solid rgba(255, 255, 255, 0.1);
`;

const AcceptanceHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h2 {
    color: #ffffff;
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: #94a3b8;
    font-size: 1rem;
  }
`;

const AcceptedStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding: 2rem;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 16px;
  max-width: 500px;
  margin: 0 auto;

  svg {
    color: #10b981;
    font-size: 2.5rem;
  }

  h3 {
    color: #10b981;
    margin-bottom: 0.25rem;
  }

  p {
    color: #34d399;
    margin: 0;
  }
`;

const AcceptanceContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const AcceptanceNotice = styled.div`
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0;
  display: flex;
  gap: 1rem;

  svg {
    color: #f59e0b;
    font-size: 1.5rem;
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  strong {
    color: #f59e0b;
  }

  div {
    color: #fbbf24;
    line-height: 1.6;
  }
`;

const AcceptanceActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DeclineButton = styled.button`
  padding: 1rem 2rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 12px;
  color: #ef4444;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
    transform: translateY(-2px);
  }
`;

const AcceptButton = styled.button`
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #10b981, #059669);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
  }
`;

const ScrollNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  color: #8b5cf6;
  font-size: 0.9375rem;
  font-weight: 500;

  svg {
    animation: ${fadeIn} 2s ease-in-out infinite;
  }
`;

export default TermsAndConditions;
